import { createStore, useStore } from 'zustand';

type LibraryFiltersState = {
  /** The cancer type the list is filtered to, 0 for all. */
  cancerTypeId: number;
  /**
   * The search box's text as typed. The list fetches with a debounced copy, so
   * this can run ahead of the list on screen for a moment.
   */
  searchQuery: string;
  showSavedPosts: boolean;
  showDisclaimerModal: boolean;
};

type LibraryFiltersActions = {
  setCancerTypeId: (cancerTypeId: number) => void;
  setSearchQuery: (searchQuery: string) => void;
  setShowSavedPosts: (showSavedPosts: boolean) => void;
  toggleShowSavedPosts: () => void;
  setShowDisclaimerModal: (showDisclaimerModal: boolean) => void;
};

export type LibraryFiltersStore = LibraryFiltersState & LibraryFiltersActions;

export const INITIAL_LIBRARY_FILTERS: LibraryFiltersState = {
  cancerTypeId: 0,
  searchQuery: '',
  showSavedPosts: false,
  showDisclaimerModal: false,
};

export const createLibraryFiltersStore = () =>
  createStore<LibraryFiltersStore>()((set) => ({
    ...INITIAL_LIBRARY_FILTERS,
    setCancerTypeId: (cancerTypeId) => set({ cancerTypeId }),
    setSearchQuery: (searchQuery) => set({ searchQuery }),
    setShowSavedPosts: (showSavedPosts) => set({ showSavedPosts }),
    toggleShowSavedPosts: () => set((state) => ({ showSavedPosts: !state.showSavedPosts })),
    setShowDisclaimerModal: (showDisclaimerModal) => set({ showDisclaimerModal }),
  }));

/**
 * The library's filters and its disclaimer modal, shared by the list, the
 * post screen, the web sidebar and the like and save hooks.
 *
 * One app-wide store, as the atoms it replaces were: a filter set on the list
 * must still apply when the reader opens a post and comes back, and the like
 * and save hooks must patch the list the reader came from.
 */
export const libraryFiltersStore = createLibraryFiltersStore();

/** Reads one slice of the library filters. Select narrowly; never the whole store. */
export function useLibraryFilters<Selected>(selector: (state: LibraryFiltersStore) => Selected) {
  return useStore(libraryFiltersStore, selector);
}
